'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import {
  formatDateToStringNY,
  createDateInNY,
} from '@/lib/timezone';

export interface FilterState {
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  organizationIds: string[];  // Changed from string | null
  productIds: string[];       // Changed from string | null
  patientState: string | null;
}

interface FilterContextType {
  filters: FilterState;
  setDateRange: (from: Date | null, to: Date | null) => void;
  setOrganizationIds: (ids: string[]) => void;
  setProductIds: (ids: string[]) => void;
  setPatientState: (state: string | null) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  isHydrated: boolean;
  refreshTrigger: number;
  triggerRefresh: () => void;
  // Legacy single-value getters for backwards compatibility
  organizationId: string | null;
  productId: string | null;
}

const defaultFilters: FilterState = {
  dateRange: {
    from: null,
    to: null,
  },
  organizationIds: [],
  productIds: [],
  patientState: null,
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

// Helper function to create date from string in New York timezone (YYYY-MM-DD format)
function createLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return createDateInNY(year, month, day, 0, 0, 0);
}

// Helper to parse comma-separated values from URL
function parseCommaSeparated(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').filter(Boolean);
}

// Helper to serialize array to comma-separated string
function toCommaSeparated(values: string[]): string | null {
  if (values.length === 0) return null;
  return values.join(',');
}

export function FilterProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [isHydrated, setIsHydrated] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Track if we should sync to URL (skip during initial hydration from URL)
  const shouldSyncToUrl = useRef(false);
  const isInitialMount = useRef(true);

  // Hydrate from localStorage after mount
  useEffect(() => {
    const saved = localStorage.getItem('dashboard-filters');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        let fromDate: Date | null = null;
        let toDate: Date | null = null;

        if (parsed.dateRange?.from) {
          const dateStr = parsed.dateRange.from;
          if (dateStr.includes('T')) {
            const date = new Date(dateStr);
            fromDate = createDateInNY(date.getFullYear(), date.getMonth() + 1, date.getDate(), 0, 0, 0);
          } else {
            fromDate = createLocalDate(dateStr);
          }
        }

        if (parsed.dateRange?.to) {
          const dateStr = parsed.dateRange.to;
          if (dateStr.includes('T')) {
            const date = new Date(dateStr);
            toDate = createDateInNY(date.getFullYear(), date.getMonth() + 1, date.getDate(), 23, 59, 59);
            toDate.setMilliseconds(999);
          } else {
            const [year, month, day] = dateStr.split('-').map(Number);
            toDate = createDateInNY(year, month, day, 23, 59, 59);
            toDate.setMilliseconds(999);
          }
        }

        // Handle backwards compatibility: old format used organizationId/productId
        let orgIds: string[] = [];
        let prodIds: string[] = [];

        if (parsed.organizationIds && Array.isArray(parsed.organizationIds)) {
          orgIds = parsed.organizationIds;
        } else if (parsed.organizationId) {
          orgIds = [parsed.organizationId];
        }

        if (parsed.productIds && Array.isArray(parsed.productIds)) {
          prodIds = parsed.productIds;
        } else if (parsed.productId) {
          prodIds = [parsed.productId];
        }

        // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid hydration from localStorage
        setFilters({
          dateRange: { from: fromDate, to: toDate },
          organizationIds: orgIds,
          productIds: prodIds,
          patientState: parsed.patientState || null,
        });
      } catch {
        setFilters(defaultFilters);
      }
    }
    setIsHydrated(true);
  }, []);

  // Parse URL params on mount (URL params take precedence)
  useEffect(() => {
    const fromParam = searchParams.get('from');
    const toParam = searchParams.get('to');
    const orgParam = searchParams.get('orgs') || searchParams.get('org'); // Support both formats
    const productParam = searchParams.get('products') || searchParams.get('product');
    const stateParam = searchParams.get('state');

    if (fromParam || toParam || orgParam || productParam || stateParam) {
      let fromDate: Date | null = null;
      let toDate: Date | null = null;

      if (fromParam) {
        fromDate = createLocalDate(fromParam);
      }

      if (toParam) {
        const [year, month, day] = toParam.split('-').map(Number);
        toDate = createDateInNY(year, month, day, 23, 59, 59);
        toDate.setMilliseconds(999);
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid hydration from URL params
      setFilters({
        dateRange: { from: fromDate, to: toDate },
        organizationIds: parseCommaSeparated(orgParam),
        productIds: parseCommaSeparated(productParam),
        patientState: stateParam,
      });
    }
  }, [searchParams]);

  // Save to localStorage whenever filters change
  useEffect(() => {
    if (!isHydrated) return;

    const fromStr = formatDateToStringNY(filters.dateRange.from);
    const toStr = formatDateToStringNY(filters.dateRange.to);

    localStorage.setItem('dashboard-filters', JSON.stringify({
      dateRange: { from: fromStr, to: toStr },
      organizationIds: filters.organizationIds,
      productIds: filters.productIds,
      patientState: filters.patientState,
    }));
  }, [filters, isHydrated]);

  // Sync to URL when filters change (after user interaction, not on mount)
  useEffect(() => {
    if (!isHydrated) return;

    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only sync if explicitly requested
    if (!shouldSyncToUrl.current) return;
    shouldSyncToUrl.current = false;

    const params = new URLSearchParams();
    if (filters.dateRange.from) {
      const fromStr = formatDateToStringNY(filters.dateRange.from);
      if (fromStr) params.set('from', fromStr);
    }
    if (filters.dateRange.to) {
      const toStr = formatDateToStringNY(filters.dateRange.to);
      if (toStr) params.set('to', toStr);
    }
    const orgsStr = toCommaSeparated(filters.organizationIds);
    if (orgsStr) params.set('orgs', orgsStr);
    const prodsStr = toCommaSeparated(filters.productIds);
    if (prodsStr) params.set('products', prodsStr);
    if (filters.patientState) params.set('state', filters.patientState);

    const queryString = params.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false });
  }, [filters, isHydrated, pathname, router]);

  const setDateRange = useCallback((from: Date | null, to: Date | null) => {
    let normalizedFrom: Date | null = null;
    let normalizedTo: Date | null = null;

    let fromYear: number | null = null;
    let fromMonth: number | null = null;
    let fromDay: number | null = null;
    let toYear: number | null = null;
    let toMonth: number | null = null;
    let toDay: number | null = null;

    if (from) {
      fromYear = from.getFullYear();
      fromMonth = from.getMonth() + 1;
      fromDay = from.getDate();
    }

    if (to) {
      toYear = to.getFullYear();
      toMonth = to.getMonth() + 1;
      toDay = to.getDate();
    }

    // Swap if from > to
    if (fromYear !== null && toYear !== null && fromMonth !== null && toMonth !== null && fromDay !== null && toDay !== null) {
      if (fromYear > toYear ||
          (fromYear === toYear && fromMonth > toMonth) ||
          (fromYear === toYear && fromMonth === toMonth && fromDay > toDay)) {
        [fromYear, toYear] = [toYear, fromYear];
        [fromMonth, toMonth] = [toMonth, fromMonth];
        [fromDay, toDay] = [toDay, fromDay];
      }
    }

    if (fromYear !== null && fromMonth !== null && fromDay !== null) {
      normalizedFrom = createDateInNY(fromYear, fromMonth, fromDay, 0, 0, 0);
    }

    if (toYear !== null && toMonth !== null && toDay !== null) {
      normalizedTo = createDateInNY(toYear, toMonth, toDay, 23, 59, 59);
      normalizedTo.setMilliseconds(999);
    }

    shouldSyncToUrl.current = true;
    setFilters(prev => ({ ...prev, dateRange: { from: normalizedFrom, to: normalizedTo } }));
  }, []);

  const setOrganizationIds = useCallback((ids: string[]) => {
    shouldSyncToUrl.current = true;
    setFilters(prev => ({ ...prev, organizationIds: ids }));
  }, []);

  const setProductIds = useCallback((ids: string[]) => {
    shouldSyncToUrl.current = true;
    setFilters(prev => ({ ...prev, productIds: ids }));
  }, []);

  const setPatientState = useCallback((state: string | null) => {
    shouldSyncToUrl.current = true;
    setFilters(prev => ({ ...prev, patientState: state }));
  }, []);

  const resetFilters = useCallback(() => {
    shouldSyncToUrl.current = true;
    setFilters(defaultFilters);
  }, []);

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const activeFilterCount = [
    filters.dateRange.from || filters.dateRange.to,
    filters.organizationIds.length > 0,
    filters.productIds.length > 0,
    filters.patientState,
  ].filter(Boolean).length;

  // Legacy single-value getters for backwards compatibility with existing code
  const organizationId = filters.organizationIds.length === 1 ? filters.organizationIds[0] : null;
  const productId = filters.productIds.length === 1 ? filters.productIds[0] : null;

  return (
    <FilterContext.Provider
      value={{
        filters,
        setDateRange,
        setOrganizationIds,
        setProductIds,
        setPatientState,
        resetFilters,
        activeFilterCount,
        isHydrated,
        refreshTrigger,
        triggerRefresh,
        // Legacy getters
        organizationId,
        productId,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
